import { useSnackbar } from "vue-haystack";

import SCAToast from "./SCAToast.vue";

/**
 * Redeclared rather than imported from SCAToast.vue: types exported from an SFC resolve as
 * `error`-typed for typescript-eslint, which then flags every use. The union is still checked
 * against SCAToast's `intent` prop at the `push` call below.
 */
export type ToastIntent = "info" | "success" | "warning" | "error";

/**
 * Lifetime of every toast, in milliseconds.
 *
 * One value for all intents. Per-call overrides are deliberately unsupported — the duration is
 * not part of `showToast`'s signature — because a spread of durations is exactly what this
 * constant replaced.
 *
 * vue-haystack treats a duration of 0 (or `undefined`) as "never auto-close", so it must always
 * be passed explicitly.
 */
export const TOAST_DURATION_MS = 5000;

/**
 * The only place a toast is created, app and libs alike.
 *
 * `time` is a literal here — never taken from caller options, never spread over — because a
 * missing or `undefined` duration produces a toast that never closes.
 *
 * App code should not call this directly: use the `showToastFor*` helpers in
 * `@/utils/toast`, which add API-error shaping and i18n on top. This is the entry point for
 * component-library code, which cannot reach the app-level helpers.
 */
export function showToast(text: string, intent: ToastIntent) {
  const snackbar = useSnackbar();

  return snackbar.push(
    SCAToast,
    {
      text,
      closeable: true,
      emphasis: "high",
      intent,
    },
    undefined,
    { time: TOAST_DURATION_MS },
  );
}
